import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, matchPath, useLocation, useNavigate } from 'react-router';
import classNames from 'clsx';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { ChevronRightRounded, VolumeOffRounded, VolumeUpRounded } from '@mui/icons-material';

import { INLINE_CHANNEL_INFO_ID } from '../../../common/components/CustomInjectionBlock/constants';
import { CHANNEL_LIST_PATH, DEFAULT_VIEW_PATH } from '../../../common/constants/routePath';
import { ALL_SOCIAL_ENGAGEMENT_TYPES, VIDEO_SOCIAL_MAP } from '../../../common/constants/videoEngagement';
import { useChannelConfig } from '@/modules/ChannelList/constants/carousels';
import { INLINE_PLAYER_INSTANCE_KEY, MAIN_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { VIEW_ALL } from '@/modules/common/components/Player/constants/customEvents';

import { ChannelType } from '@/modules/common/types/channel';

import { abbreviateNumber } from '../../../common/helpers/number';
import { isWatchInIframe } from '../../../common/helpers/routes';
import { getVideoEngagementData } from '../../../common/helpers/video';
import * as commonSelectors from '../../../common/redux/selectors/common';
import { usePlayerWidget } from '@/modules/common/components/Player/helpers';
import { isVisibleInConfig } from '@/modules/common/helpers/configuration';
import { getFormattedDuration } from '@/modules/common/helpers/dateTime';
import { getPathNameByParams } from '@/modules/common/helpers/history';
import { useTranslate } from '@/modules/common/helpers/i18n';

import ConfigurationIcon from '@/modules/common/components/ConfigurationIcon/ConfigurationIcon';
import CustomInjectionBlock from '../../../common/components/CustomInjectionBlock/CustomInjectionBlock';
import SocialButtons from '../../../common/components/SocialButtons/SocialButtons';
import Carousel, { Skeleton as CarouselSkeleton } from '../Carousel/Carousel';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import { Button, Link, Stack, Typography } from '@/mui/components';

import styles from './InlineCarousel.module.scss';

type Props = {
  bg: string;
  bgLogo: string;
  titleColor: string;
  descColor: string;
  vidTitleColor: string;
  vidInfoColor: string;
  channel: ChannelType;
};

type CarouselRef = {
  scrollTo: (index: number) => number;
};

function InlineCarousel({ bg, bgLogo, titleColor, descColor, vidTitleColor, vidInfoColor, channel }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const carouselOptionsRef = useRef<CarouselRef | null>(null);
  const engagement = useSelector(commonSelectors.engagementSelector);
  const dblClickFlow = useSelector(commonSelectors.dblClickFlowSelector);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMuted, setMuteState] = useState(true);
  const LWPMain = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);
  const LWP = usePlayerWidget(INLINE_PLAYER_INSTANCE_KEY);
  const configuration = useSelector(commonSelectors.configurationSelector);
  const dateFormat = configuration.common.dateTimeFormat;
  const configByChannel = useChannelConfig();
  const t = useTranslate();

  const titleConfig = configByChannel.title;
  const viewAllConfig = configByChannel.viewAll;
  const socialStatConfig = configuration.common.videoEngagement;

  const videoCardTitleConfig = configByChannel.videoCard.title;
  const videoCardDescriptionConfig = configByChannel.videoCard.description;
  const videoCardDateTimeConfig = configByChannel.videoCard.dateTime;
  const cardSocialStatConfig = configByChannel.videoCard.videoEngagement;
  const videoCardDurationConfig = configByChannel.videoCard.duration;

  const showSocialButtons = isVisibleInConfig(configuration.common.videoActions.visible);
  const selectedVideo = channel.playlist[selectedIndex];

  useEffect(() => {
    setSelectedIndex(0);
  }, [channel.playlist]);

  useEffect(() => {
    if (!matchPath(CHANNEL_LIST_PATH, location.pathname)) {
      setMuteState(true);
      LWP.muted(true);
      LWP.pause();
    } else {
      LWP.play();
    }
  }, [selectedVideo?.mediaid, location.pathname]);

  useEffect(
    () => () => {
      LWP.muted(true);
    },
    [],
  );

  const videoEngagement = Object.entries(
    selectedVideo ? getVideoEngagementData(selectedVideo.performanceData.parameters, engagement) : {},
  ).reduce(
    (acc, [type$, value]) => {
      const type = type$ as (typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number];

      const { configKey } = VIDEO_SOCIAL_MAP[type];

      if (isVisibleInConfig(socialStatConfig[configKey].visible, cardSocialStatConfig[configKey].visible)) {
        acc[type] = value;
      }

      return acc;
    },
    {} as Record<(typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number], number>,
  );

  const VolumeIcon = isMuted ? VolumeOffRounded : VolumeUpRounded;

  return (
    <div
      className={styles.Wrapper}
      data-a="channel-type-inline"
      aria-label={t.videosByChannel.text()}
      style={
        {
          '--sc-bg-color': bg,
          '--sc-logo-bg-color': bgLogo,
          '--sc-title-color': titleColor,
          '--sc-desc-color': descColor,
          '--sc-vid-title-color': vidTitleColor,
          '--sc-vid-info-color': vidInfoColor,
        } as CSSProperties
      }
    >
      {selectedVideo && (
        <>
          <VideoPlayer
            className={styles.VideoWrapper}
            video={selectedVideo}
            channel={channel}
            onEnded={() => {
              if (carouselOptionsRef.current) {
                const nextIndex = carouselOptionsRef.current.scrollTo(selectedIndex);

                setSelectedIndex(nextIndex);
              }
            }}
          />
          <div
            aria-label={t.showMore.text()}
            data-a="player-gradient"
            className={styles.Gradient}
            style={
              {
                '--internal-color': theme.palette.background.default,
              } as CSSProperties
            }
          />
        </>
      )}
      <Stack direction="row" alignItems="stretch" className={styles.Info} fullWidth>
        <div className={styles.InfoContent}>
          <CustomInjectionBlock id={INLINE_CHANNEL_INFO_ID} />
          {selectedVideo && (
            <Stack spacing={4}>
              {isVisibleInConfig(videoCardTitleConfig.visible) && (
                <Typography
                  style={{
                    fontFamily: videoCardTitleConfig.fontFamily,
                  }}
                  data-a="video-title"
                  title={selectedVideo.title}
                  rows={3}
                  variant={videoCardTitleConfig.typography ?? 'h6'}
                  fontWeight={videoCardTitleConfig.fontWeight}
                  color={videoCardTitleConfig.color}
                >
                  {selectedVideo.title}
                </Typography>
              )}
              <Stack spacing={4} direction="row" alignItems="center">
                <Stack spacing={2} direction="row" alignItems="center">
                  {isVisibleInConfig(videoCardDateTimeConfig.visible) && (
                    <Typography
                      style={{
                        fontFamily: videoCardDateTimeConfig.fontFamily,
                      }}
                      variant={videoCardDateTimeConfig.typography ?? 'body2'}
                      data-a="video-date-time"
                      fontWeight={videoCardDateTimeConfig.fontWeight}
                      color={videoCardDateTimeConfig.color}
                    >
                      {dayjs(selectedVideo.created ?? 0).format(dateFormat)}
                    </Typography>
                  )}
                  {isVisibleInConfig(videoCardDurationConfig.visible) && (
                    <Typography
                      style={{
                        fontFamily: videoCardDurationConfig.fontFamily,
                        backgroundColor: videoCardDurationConfig.backgroundColor,
                      }}
                      variant={videoCardDurationConfig.typography ?? 'body2'}
                      data-a="video-duration"
                      className={classNames(styles.Duration, {
                        [styles.Duration___default]:
                          !videoCardDurationConfig.color && !videoCardDurationConfig.backgroundColor,
                      })}
                      fontWeight={videoCardDurationConfig.fontWeight}
                      color={videoCardDurationConfig.color}
                    >
                      {getFormattedDuration(selectedVideo.duration ?? 0)}
                    </Typography>
                  )}
                </Stack>
                {!!Object.keys(videoEngagement).length && (
                  <Stack spacing={4} direction="row" alignItems="center" data-a="social-statistic">
                    {Object.keys(videoEngagement).map((type$) => {
                      const type = type$ as (typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number];

                      const { configKey, icon } = VIDEO_SOCIAL_MAP[type];

                      const fontFamily =
                        cardSocialStatConfig[configKey].fontFamily ?? socialStatConfig[configKey].fontFamily;
                      const fontWeight =
                        cardSocialStatConfig[configKey].fontWeight ?? socialStatConfig[configKey].fontWeight;
                      const typography =
                        cardSocialStatConfig[configKey].typography ?? socialStatConfig[configKey].typography;
                      const color = cardSocialStatConfig[configKey].color ?? socialStatConfig[configKey].color;
                      const iconKey = cardSocialStatConfig[configKey].icon ?? socialStatConfig[configKey].icon;

                      return (
                        <Typography
                          key={type}
                          className={styles.VideoEngagement}
                          variant={typography ?? 'captionSmall'}
                          fontWeight={fontWeight}
                          style={{ fontFamily }}
                        >
                          <ConfigurationIcon
                            className={styles.VideoEngagementIcon}
                            iconKey={iconKey}
                            fallback={icon}
                            color={color}
                            fontSize="inherit"
                          />
                          {abbreviateNumber(videoEngagement[type])}
                        </Typography>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
              {showSocialButtons && !isWatchInIframe && (
                <SocialButtons
                  video={selectedVideo}
                  routePath={DEFAULT_VIEW_PATH}
                  channelId={channel.id}
                  channelType={channel.type}
                  fromInlineChannel
                />
              )}
              {isVisibleInConfig(videoCardDescriptionConfig.visible) && (
                <Typography
                  style={{
                    fontFamily: videoCardDescriptionConfig.fontFamily,
                  }}
                  title={selectedVideo.plot}
                  rows={10}
                  data-a="video-description"
                  component="div"
                  variant={videoCardDescriptionConfig.typography ?? 'body2'}
                  fontWeight={videoCardDescriptionConfig.fontWeight}
                  color={videoCardDescriptionConfig.color}
                >
                  {selectedVideo.plot}
                </Typography>
              )}
            </Stack>
          )}
        </div>
        <div className={styles.VolumeButtonWrapper}>
          {selectedVideo && (
            <>
              <Link
                className={styles.MainLink}
                component={RouterLink}
                aria-label={t.showMore.text()}
                to={getPathNameByParams(DEFAULT_VIEW_PATH, {
                  channel: `${channel.id}`,
                  video: selectedVideo.mediaid,
                })}
              />
              <Button
                className={styles.VolumeButton}
                data-a="player-button"
                aria-label={t.volumeButton.text()}
                type="button"
                variant="outlined"
                color="inherit"
                size="large"
                shape="circular"
                square
                onClick={(event) => {
                  event.preventDefault();

                  setMuteState((prev) => {
                    const muted = !prev;
                    LWP.muted(muted);

                    return muted;
                  });
                }}
                startIcon={<VolumeIcon />}
              />
            </>
          )}
        </div>
      </Stack>
      <div className={styles.WrapperCarousel}>
        <Stack spacing={2} direction="row" alignContent="center" alignItems="center" className={styles.TitleWrapper}>
          {isVisibleInConfig(titleConfig.visible) ? (
            <Typography
              style={{
                fontFamily: titleConfig.fontFamily,
              }}
              title={channel.title}
              rows={1}
              data-a="channel-title"
              variant={titleConfig.typography ?? 'inherit'}
              color={titleConfig.color ?? 'inherit'}
              fontWeight={titleConfig.fontWeight}
              component={RouterLink}
              to={`/channel/${channel.id}${location.search}`}
              className={classNames(styles.Title, {
                [styles.Title___default]: !titleConfig.typography,
              })}
            >
              {channel.title}
            </Typography>
          ) : (
            <div />
          )}
          {isVisibleInConfig(viewAllConfig.visible) ? (
            <Typography
              style={{
                fontFamily: viewAllConfig.fontFamily,
              }}
              variant={viewAllConfig.typography ?? 'inherit'}
              fontWeight={viewAllConfig.fontWeight}
              color={viewAllConfig.color ?? 'inherit'}
              component={RouterLink}
              to={`/channel/${channel.id}${location.search}`}
              title={t.allVideosInTitle.text({
                title: channel.title,
              })}
              data-a="channel-title-view-all"
              onClick={() => {
                LWPMain.sendCustomEvent(VIEW_ALL);
              }}
              rows={1}
              className={styles.ViewAll}
            >
              {t.viewAll.text()}
              <ChevronRightRounded className={styles.ViewAllIcon} />
            </Typography>
          ) : (
            <div />
          )}
        </Stack>
        <div className={styles.Carousel}>
          {channel.playlist.length ? (
            <Carousel
              ref={carouselOptionsRef}
              channel={channel}
              activeSlideMediaId={selectedVideo?.mediaid}
              activeCustomContent={
                <Typography variant="caption" className={styles.PlayIndicator}>
                  {t.playingNow.text()}
                </Typography>
              }
              onSlideClick={(event, index, mediaId) => {
                if (dblClickFlow && selectedVideo?.mediaid !== mediaId) {
                  event.preventDefault();
                }

                setSelectedIndex(index);
              }}
              onSlideDoubleClick={(_, _$, mediaId) => {
                if (dblClickFlow && selectedVideo?.mediaid !== mediaId) {
                  navigate(`/channel/${channel.id}/${mediaId}${location.search}`);
                }
              }}
            />
          ) : (
            <CarouselSkeleton channel={channel} />
          )}
        </div>
      </div>
    </div>
  );
}

export default InlineCarousel;
